// Clipboard + file-download helpers (plan Item A2). The app had no
// `navigator.clipboard` / `Blob` download surface until export shipped; these are
// the first uses. Both are best-effort and degrade on old / non-secure contexts.

/**
 * Copy `text` to the clipboard, resolving `true` on success so callers can flash
 * "Copied ✓". Prefers the async Clipboard API; falls back to a hidden textarea +
 * `document.execCommand("copy")` for non-secure contexts (http, older Safari)
 * where `navigator.clipboard` is undefined or rejects.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    // Keep it out of the layout / off-screen but still selectable.
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.setAttribute("readonly", "");
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Trigger a browser download of `text` as a file named `filename`. Builds a Blob,
 * hands out an object URL via a synthetic `<a download>` click, then revokes the
 * URL. `mime` defaults to the standard chess-PGN type.
 */
export function downloadText(filename: string, text: string, mime = "application/x-chess-pgn"): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the click has definitely been dispatched.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A filesystem-safe slug of a display name (for building filenames). */
export function slugName(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "game"
  );
}
